import React, { useEffect, useState } from "react";
import { Briefcase, Plus, Trash2, GripVertical } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Loader } from "@/components/dashboard/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Icon from "@/components/Icon";

const empty = { title: "", description: "", icon: "Sparkles", cta: "Faire une demande", featured: false, bookable: false, custom_request: true, requires_account: false, active: true };

export default function AdminServices() {
  const [services, setServices] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const load = () => api.get("/services?all=true").then(({ data }) => setServices(data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setF({ ...s }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/services/${editing.service_id}`, f); else await api.post("/services", f);
      toast.success("Service enregistré"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (s) => { if (!window.confirm("Supprimer ce service ?")) return; await api.delete(`/services/${s.service_id}`); load(); };
  const move = async (idx, dir) => {
    const arr = [...services]; const j = idx + dir; if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; setServices(arr);
    await api.post("/services/reorder", { order: arr.map((s) => s.service_id) });
  };
  if (services === null) return <Loader />;
  return (
    <div>
      <PageHeader title="Services" subtitle="Gérez le catalogue affiché sur le site public."
        action={<Button className="bg-primary" onClick={openNew} data-testid="add-service-btn"><Plus className="h-4 w-4 mr-1.5" />Ajouter</Button>} />
      <div className="space-y-2">{services.map((s, idx) => (
        <div key={s.service_id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`admin-service-${s.service_id}`}>
          <div className="flex flex-col"><button onClick={() => move(idx, -1)} className="text-slate-300 hover:text-slate-600 text-xs">▲</button><button onClick={() => move(idx, 1)} className="text-slate-300 hover:text-slate-600 text-xs">▼</button></div>
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Icon name={s.icon} className="h-5 w-5 text-primary" /></div>
          <div className="min-w-0 flex-1"><p className="font-medium text-slate-900 truncate">{s.title}</p><p className="text-sm text-slate-400 truncate">{s.description}</p></div>
          <div className="flex items-center gap-2 shrink-0">
            {s.featured && <span className="text-xs px-2 py-0.5 rounded-full bg-accent/10 text-accent">Vedette</span>}
            {!s.active && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactif</span>}
            <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-service-${s.service_id}`}>Modifier</Button>
            <button onClick={() => del(s)}><Trash2 className="h-4 w-4 text-rose-400 hover:text-rose-600" /></button>
          </div>
        </div>))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto custom-scroll">
          <DialogHeader><DialogTitle>{editing ? "Modifier le service" : "Nouveau service"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Titre</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" data-testid="service-title" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1" data-testid="service-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Icône (lucide)</Label><Input value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} className="mt-1" placeholder="Phone, Mail, Briefcase..." /></div>
              <div><Label>Libellé du bouton (CTA)</Label><Input value={f.cta} onChange={(e) => setF({ ...f, cta: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <label className="flex items-center justify-between"><span className="text-sm">Réservable au calendrier</span><Switch checked={f.bookable} onCheckedChange={(v) => setF({ ...f, bookable: v })} data-testid="service-bookable" /></label>
              <label className="flex items-center justify-between"><span className="text-sm">Demande personnalisée</span><Switch checked={f.custom_request} onCheckedChange={(v) => setF({ ...f, custom_request: v })} /></label>
              <label className="flex items-center justify-between"><span className="text-sm">Compte requis</span><Switch checked={f.requires_account} onCheckedChange={(v) => setF({ ...f, requires_account: v })} /></label>
              <label className="flex items-center justify-between"><span className="text-sm">Mis en avant (vedette)</span><Switch checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} data-testid="service-featured" /></label>
              <label className="flex items-center justify-between"><span className="text-sm">Actif</span><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></label>
            </div>
          </div>
          <DialogFooter><Button className="bg-primary" onClick={save} data-testid="service-save">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
